import React, { useState, useCallback, useEffect } from 'react'
import { ExternalLink } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import Button from '@/components/ui/Button'
import { useAppSelector } from '@/store/hooks'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import { OrganizationService } from '@/api/generated/services/OrganizationService'
import type { OrgAcademySettingResponse } from '@/api/generated/models/OrgAcademySettingResponse'
import SchoolComparisonTab from './components/SchoolComparisonTab'
import DepartmentDataTab from './components/DepartmentDataTab'
import SchoolSettingModal from './components/SchoolSettingModal'

type TabType = 'comparison' | 'department'

const TABS: { key: TabType; label: string }[] = [
  { key: 'comparison', label: '학교 비교' },
  { key: 'department', label: '학과별 자료' },
]

const ACADEMYINFO_DATA_URL = 'https://www.academyinfo.go.kr/popup/main0810/list.do'

const AcademyInfoPage: React.FC = () => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const orgId = selectedWorkspace?.org_id || ''

  const [activeTab, setActiveTab] = useState<TabType>('comparison')
  const [selectedYear, setSelectedYear] = useState('')
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [orgSetting, setOrgSetting] = useState<OrgAcademySettingResponse | null>(null)
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch org setting + available years on mount
  const fetchInitialData = useCallback(async () => {
    if (!orgId) return
    setIsLoading(true)
    setError(null)

    try {
      // orgId 전달 → 기관의 hidden_years 가 자동 제외됨
      // orgRes 의 resolved_default_year 를 기본 선택값으로 우선 사용
      const [settingRes, yearsRes, orgRes] = await Promise.all([
        AcademyinfoService.appsAcademyinfoViewsGetOrgSetting({ orgId }),
        AcademyinfoService.appsAcademyinfoViewsGetAvailableYears({ orgId }),
        OrganizationService.appsOrganizationViewsGetOrg({ orgId }),
      ])
      setOrgSetting(settingRes)
      const years = yearsRes.years || []
      setAvailableYears(years)
      if (years.length > 0 && !selectedYear) {
        const preferred = orgRes.resolved_default_year && years.includes(orgRes.resolved_default_year)
          ? orgRes.resolved_default_year
          : years[0]
        setSelectedYear(preferred)
      }
    } catch (err) {
      console.error('Failed to fetch academy info:', err)
      setError('데이터를 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData])

  const myAcademy = orgSetting?.my_academy || null
  const compareAcademies = orgSetting?.compare_academies || []

  const handleSettingSaved = useCallback(async () => {
    setIsSettingModalOpen(false)
    // Refresh org setting after save
    try {
      const settingRes = await AcademyinfoService.appsAcademyinfoViewsGetOrgSetting({ orgId })
      setOrgSetting(settingRes)
    } catch (err) {
      console.error('Failed to refresh org setting:', err)
    }
  }, [orgId])

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '16px', color: designTokens.colors.gray[4] }}>
          로딩 중...
        </span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '16px', color: designTokens.colors.error.DEFAULT }}>
          {error}
        </span>
      </div>
    )
  }

  const tabSwitcher = (
    <div
      className="flex items-center rounded-lg overflow-hidden flex-shrink-0"
      style={{ border: `1px solid ${designTokens.colors.gray[2]}` }}
    >
      {TABS.map((tab, idx) => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          style={{
            padding: '6px 20px',
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: activeTab === tab.key ? 600 : 400,
            color: activeTab === tab.key ? '#fff' : designTokens.colors.gray[5],
            backgroundColor: activeTab === tab.key
              ? designTokens.colors.primary.DEFAULT
              : '#fff',
            border: 'none',
            borderRight: idx < TABS.length - 1
              ? `1px solid ${designTokens.colors.gray[2]}`
              : 'none',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )

  const handleOpenAcademyinfoData = () => {
    window.open(ACADEMYINFO_DATA_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Page Header */}
      <div className="flex items-center justify-end mb-3 flex-shrink-0">
        <Button
          size="small"
          color="white"
          onClick={handleOpenAcademyinfoData}
          className="flex items-center gap-1"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          대학알리미 공시 DATA 다운로드
        </Button>
      </div>

      {/* No school configured */}
      {!myAcademy ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '16px', color: designTokens.colors.gray[4] }}>
            내 학교가 설정되지 않았습니다.
          </span>
          <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
            사이드바 설정 &gt; 기관 관리에서 내 학교를 설정해주세요.
          </span>
        </div>
      ) : (
        /* Tab Content */
        <div className="flex-1 overflow-hidden">
          {activeTab === 'comparison' && (
            <SchoolComparisonTab
              orgId={orgId}
              myAcademy={myAcademy}
              compareAcademies={compareAcademies}
              selectedYear={selectedYear}
              availableYears={availableYears}
              onYearChange={setSelectedYear}
              onOpenSettings={() => setIsSettingModalOpen(true)}
              tabSwitcher={tabSwitcher}
            />
          )}
          {activeTab === 'department' && (
            <DepartmentDataTab
              myAcademy={myAcademy}
              selectedYear={selectedYear}
              availableYears={availableYears}
              onYearChange={setSelectedYear}
              tabSwitcher={tabSwitcher}
            />
          )}
        </div>
      )}

      {/* School Setting Modal */}
      <SchoolSettingModal
        isOpen={isSettingModalOpen}
        onClose={() => setIsSettingModalOpen(false)}
        orgId={orgId}
        currentMyAcademy={myAcademy}
        currentCompareAcademies={compareAcademies}
        onSaved={handleSettingSaved}
      />
    </div>
  )
}

export default AcademyInfoPage
